import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { flows, quiz } from '../../services/api';
import type {
  Flow,
  Question,
  QuizAttempt,
  QuizIntroResponse,
  QuizResponse,
  QuizSubmissionResponse,
  ResponsePayload,
} from '../../types';
import { isQuestionResponseValid } from 'utils/questionResponseValidation';
import { normalizeQuestionType } from 'utils/questionTypeUtils';
import { useLatestRef } from '../../hooks/useLatestRef';
import {
  areResponsesEqual,
  mapApiResponseToStored,
  storedToDraft,
  type FlowResponseDraft,
  type StoredResponse,
} from './responseUtils';
import { getErrorMessage } from './questionSessionHelpers';
import { QuizFlowInitialBootstrap } from './types';

interface FlowUser {
  id: number;
}

interface UseQuizAttemptManagerParams {
  flowName: string;
  initialFlow?: Flow | null;
  initialBootstrap?: QuizFlowInitialBootstrap | null;
  user: FlowUser | null;
  /** The course the quiz was opened from; picks the course theme the quiz inherits. */
  courseId?: number | null;
}

interface QuizProgressState {
  attemptId: number | null;
  status: 'not_started' | 'in_progress' | 'completed';
  currentIndex: number;
  totalQuestions: number;
  completedAt: string | null;
}

export type SubmitQuizOptions = {
  includeQuestionReview?: boolean;
};

interface UseQuizAttemptManagerResult {
  loading: boolean;
  error: string | null;
  autosaveError: string | null;
  isAccessDenied: boolean;
  hasReachedMaxAttempts: boolean;
  isRetakeBlocked: boolean;
  isQuizEntryBlocked: boolean;
  hasStartedAttempt: boolean;
  flow: Flow | null;
  intro: QuizIntroResponse | null;
  attempt: QuizAttempt | null;
  questions: Question[];
  currentQuestionIndex: number;
  currentQuestion: Question | null;
  responsesByQuestion: Record<number, FlowResponseDraft>;
  hasUnsavedChanges: boolean;
  isSubmitting: boolean;
  isSavingResponse: boolean;
  isFlowCompleted: boolean;
  progress: QuizProgressState;
  loadIntro: () => Promise<void>;
  startOrResumeAttempt: () => Promise<void>;
  goToPreviousQuestion: () => void;
  goToQuestion: (index: number) => void;
  clearAutosaveError: () => void;
  submitQuestionResponse: (response: ResponsePayload, options?: { advance?: boolean }) => Promise<void>;
  submitQuiz: (options?: SubmitQuizOptions) => Promise<QuizSubmissionResponse>;
}

const orderQuestions = (
  questions: Question[],
  questionOrder: number[] | undefined,
): Question[] => {
  if (!Array.isArray(questionOrder) || questionOrder.length === 0) {
    return questions;
  }

  const byId = new Map<number, Question>();
  questions.forEach((question) => {
    if (typeof question.id === 'number') {
      byId.set(question.id, question);
    }
  });

  const ordered: Question[] = [];
  const seen = new Set<number>();

  questionOrder.forEach((questionId) => {
    if (typeof questionId !== 'number' || seen.has(questionId)) {
      return;
    }

    const question = byId.get(questionId);
    if (!question) {
      return;
    }

    ordered.push(question);
    seen.add(questionId);
  });

  questions.forEach((question) => {
    if (typeof question.id !== 'number' || seen.has(question.id)) {
      return;
    }
    ordered.push(question);
  });

  return ordered;
};

const orderQuestionOptions = (
  question: Question,
  optionOrderByQuestion: Record<string, number[]> | undefined,
): Question => {
  if (!Array.isArray(question.options) || !question.options.length || !optionOrderByQuestion) {
    return question;
  }

  const questionId = question.id;
  if (typeof questionId !== 'number') {
    return question;
  }

  const orderedOptionIds = optionOrderByQuestion[String(questionId)];
  if (!Array.isArray(orderedOptionIds) || !orderedOptionIds.length) {
    return question;
  }

  const optionsById = new Map<number, NonNullable<Question['options']>[number]>();
  question.options.forEach((option) => {
    optionsById.set(option.id, option);
  });

  const orderedOptions: NonNullable<Question['options']> = [];
  const seen = new Set<number>();

  orderedOptionIds.forEach((optionId) => {
    if (typeof optionId !== 'number' || seen.has(optionId)) {
      return;
    }
    const option = optionsById.get(optionId);
    if (!option) {
      return;
    }
    orderedOptions.push(option);
    seen.add(optionId);
  });

  question.options.forEach((option) => {
    if (seen.has(option.id)) {
      return;
    }
    orderedOptions.push(option);
  });

  return {
    ...question,
    options: orderedOptions,
  };
};

const applyAttemptLayout = (
  questions: Question[],
  attempt: QuizAttempt | null,
): Question[] => {
  const orderedQuestions = orderQuestions(questions, attempt?.question_order);
  return orderedQuestions.map((question) => orderQuestionOptions(question, attempt?.option_order));
};

const buildSubmissionResponse = (
  question: Question,
  draft: FlowResponseDraft,
): QuizResponse => {
  const questionId = question.id;
  if (typeof questionId !== 'number') {
    throw new Error('Invalid quiz question.');
  }

  const normalizedQuestionType = normalizeQuestionType(
    question.question_type_display ?? question.question_type ?? '',
  );

  const payload: QuizResponse = { question_id: questionId };

  if (normalizedQuestionType === 'multiple_choice' || normalizedQuestionType === 'multiple_select') {
    payload.selected_options = Array.isArray(draft.selected_options)
      ? draft.selected_options.filter((optionId): optionId is number => typeof optionId === 'number')
      : [];
    return payload;
  }

  if (normalizedQuestionType === 'boolean') {
    if (typeof draft.boolean_value !== 'boolean') {
      throw new Error(`Question ${questionId} requires a true/false answer.`);
    }
    payload.boolean_value = draft.boolean_value;
    return payload;
  }

  if (typeof draft.text_value === 'string') {
    payload.text_value = draft.text_value;
  }
  if (typeof draft.rating_value === 'number') {
    payload.rating_value = draft.rating_value;
  }
  if (typeof draft.boolean_value === 'boolean') {
    payload.boolean_value = draft.boolean_value;
  }

  return payload;
};

const deriveFirstIncompleteIndex = (
  questions: Question[],
  drafts: Record<number, FlowResponseDraft>,
) => {
  const firstIncompleteIndex = questions.findIndex((question) => {
    const questionId = question.id;
    if (typeof questionId !== 'number') {
      return true;
    }
    const draft = drafts[questionId];
    if (!draft) {
      return true;
    }

    if (draft.is_completed !== true) {
      return true;
    }

    return !isQuestionResponseValid(question, { draft });
  });

  return firstIncompleteIndex === -1 ? questions.length : firstIncompleteIndex;
};

export const useQuizAttemptManager = ({
  flowName,
  initialFlow = null,
  initialBootstrap = null,
  user,
  courseId = null,
}: UseQuizAttemptManagerParams): UseQuizAttemptManagerResult => {
  const [loading, setLoading] = useState(() => !initialBootstrap);
  const [error, setError] = useState<string | null>(() => initialBootstrap?.errorMessage ?? null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [hasReachedMaxAttempts, setHasReachedMaxAttempts] = useState(
    () => initialBootstrap?.hasReachedMaxAttempts ?? false,
  );
  const [isRetakeBlocked, setIsRetakeBlocked] = useState(
    () => initialBootstrap?.isRetakeBlocked ?? false,
  );
  const [autosaveError, setAutosaveError] = useState<string | null>(null);
  const [flowState, setFlowState] = useState<Flow | null>(initialBootstrap?.flow ?? initialFlow);
  const [introState, setIntroState] = useState<QuizIntroResponse | null>(initialBootstrap?.intro ?? null);
  const [attemptState, setAttemptState] = useState<QuizAttempt | null>(null);
  const [questionsState, setQuestionsState] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [responseDrafts, setResponseDrafts] = useState<Record<number, FlowResponseDraft>>({});
  const responseDraftsRef = useLatestRef(responseDrafts);
  const [serverResponses, setServerResponses] = useState<Record<number, StoredResponse>>({});
  const serverResponsesRef = useLatestRef(serverResponses);
  const [progressState, setProgressState] = useState<QuizProgressState>({
    attemptId: null,
    status: 'not_started',
    currentIndex: 0,
    totalQuestions: 0,
    completedAt: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inFlightSaveCount, setInFlightSaveCount] = useState(0);
  const saveQueueByQuestionRef = useRef<Map<number, Promise<void>>>(new Map());
  const saveRevisionByQuestionRef = useRef<Record<number, number>>({});
  const submitQuizPromiseRef = useRef<Promise<QuizSubmissionResponse> | null>(null);

  const commitResponseDrafts = useCallback(
    (
      updater:
        | Record<number, FlowResponseDraft>
        | ((previous: Record<number, FlowResponseDraft>) => Record<number, FlowResponseDraft>),
    ) => {
      const nextDrafts =
        typeof updater === 'function'
          ? updater(responseDraftsRef.current)
          : updater;
      responseDraftsRef.current = nextDrafts;
      setResponseDrafts(nextDrafts);
      return nextDrafts;
    },
    [responseDraftsRef],
  );

  const loadIntro = useCallback(async () => {
    if (!flowName || !user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setIsAccessDenied(false);
    setHasReachedMaxAttempts(false);
    setIsRetakeBlocked(false);
    setAutosaveError(null);

    try {
      const flowResponse = initialFlow ? { data: initialFlow } : await flows.getFlow(flowName, { courseId });
      const resolvedFlow = flowResponse?.data ?? null;
      if (!resolvedFlow?.id) {
        throw new Error('Quiz module not found.');
      }
      if (!resolvedFlow.is_quiz) {
        throw new Error('This module is not configured as a quiz.');
      }

      const introResponse = await quiz.getIntro(resolvedFlow.id);
      const introPayload = (introResponse?.data as QuizIntroResponse | undefined) ?? null;
      const blockReason = introPayload?.block_reason ?? null;

      setFlowState(resolvedFlow);
      setIntroState(introPayload);
      setAttemptState(null);
      setQuestionsState([]);
      setResponseDrafts({});
      setServerResponses({});
      responseDraftsRef.current = {};
      serverResponsesRef.current = {};
      saveQueueByQuestionRef.current = new Map();
      saveRevisionByQuestionRef.current = {};
      setCurrentIndex(0);
      setProgressState({
        attemptId: null,
        status: 'not_started',
        currentIndex: 0,
        totalQuestions: 0,
        completedAt: null,
      });

      setHasReachedMaxAttempts(blockReason === 'max_attempts');
      setIsRetakeBlocked(blockReason === 'retake_disabled_after_pass');
      if (introPayload?.can_enter_quiz === false && introPayload?.message) {
        setError(introPayload.message);
      }
    } catch (introError) {
      const responsePayload = (
        introError as {
          response?: {
            status?: number;
            data?: {
              error?: string;
              block_reason?: string;
              can_retake?: boolean;
              max_attempts?: number | null;
              attempts_used?: number;
            };
          };
        }
      )?.response?.data;
      const httpStatus = (introError as { response?: { status?: number } })?.response?.status;
      const blockReason = responsePayload?.block_reason;

      const isMaxAttemptsError =
        blockReason === 'max_attempts' ||
        (httpStatus === 400 &&
          responsePayload?.can_retake === false &&
          ((typeof responsePayload?.max_attempts === 'number' &&
            typeof responsePayload?.attempts_used === 'number' &&
            responsePayload.attempts_used >= responsePayload.max_attempts) ||
            (typeof responsePayload?.error === 'string' &&
              responsePayload.error.toLowerCase().includes('max attempts'))));
      const isRetakeBlockedError =
        blockReason === 'retake_disabled_after_pass' ||
        (httpStatus === 400 &&
          typeof responsePayload?.error === 'string' &&
          responsePayload.error.toLowerCase().includes('retakes are disabled'));

      setIsAccessDenied(httpStatus === 403);
      setHasReachedMaxAttempts(isMaxAttemptsError);
      setIsRetakeBlocked(isRetakeBlockedError);
      setError(getErrorMessage(introError));
      setFlowState(initialFlow ?? null);
      setIntroState(null);
      setAttemptState(null);
      setQuestionsState([]);
      setResponseDrafts({});
      setServerResponses({});
      responseDraftsRef.current = {};
      serverResponsesRef.current = {};
      saveQueueByQuestionRef.current = new Map();
      saveRevisionByQuestionRef.current = {};
      setCurrentIndex(0);
      setProgressState({
        attemptId: null,
        status: 'not_started',
        currentIndex: 0,
        totalQuestions: 0,
        completedAt: null,
      });
    } finally {
      setLoading(false);
    }
  }, [courseId, flowName, initialFlow, responseDraftsRef, serverResponsesRef, user?.id]);

  const startOrResumeAttempt = useCallback(async () => {
    const resolvedFlow = flowState ?? initialFlow;
    if (!resolvedFlow?.id) {
      throw new Error('Quiz module not found.');
    }

    setLoading(true);
    setError(null);
    setIsAccessDenied(false);
    setAutosaveError(null);

    try {
      const startResponse = await quiz.startQuiz(resolvedFlow.id);
      const startPayload = startResponse?.data;
      const quizAttempt = (startPayload?.quiz_attempt as QuizAttempt | undefined) ?? null;
      const attemptId = quizAttempt?.id;
      if (typeof attemptId !== 'number') {
        throw new Error('Unable to start quiz attempt.');
      }

      const rawQuestions = Array.isArray(startPayload?.questions)
        ? (startPayload?.questions as Question[])
        : [];
      const orderedQuestions = applyAttemptLayout(rawQuestions, quizAttempt);

      const rawResponses = Array.isArray(startPayload?.responses)
        ? startPayload.responses
        : [];
      const nextServerResponses: Record<number, StoredResponse> = {};
      const drafts: Record<number, FlowResponseDraft> = {};
      rawResponses.forEach((rawResponse) => {
        const stored = mapApiResponseToStored(rawResponse);
        if (!stored || typeof stored.question !== 'number') {
          return;
        }
        nextServerResponses[stored.question] = stored;
        drafts[stored.question] = storedToDraft(stored);
      });

      const derivedIndex = deriveFirstIncompleteIndex(orderedQuestions, drafts);

      setAttemptState(quizAttempt);
      setQuestionsState(orderedQuestions);
      setResponseDrafts(drafts);
      setServerResponses(nextServerResponses);
      responseDraftsRef.current = drafts;
      serverResponsesRef.current = nextServerResponses;
      saveQueueByQuestionRef.current = new Map();
      saveRevisionByQuestionRef.current = {};
      setCurrentIndex(derivedIndex);
      setProgressState({
        attemptId,
        status: 'in_progress',
        currentIndex: derivedIndex,
        totalQuestions: orderedQuestions.length,
        completedAt: null,
      });
      setHasReachedMaxAttempts(false);
      setIsRetakeBlocked(false);
      setIntroState((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          can_enter_quiz: true,
          block_reason: null,
          message: undefined,
          in_progress_attempt: {
            id: attemptId,
            attempt_number: quizAttempt?.attempt_number ?? previous.in_progress_attempt?.attempt_number ?? 1,
            started_at: quizAttempt?.started_at ?? previous.in_progress_attempt?.started_at ?? '',
          },
        };
      });
    } catch (startError) {
      const responsePayload = (
        startError as {
          response?: {
            status?: number;
            data?: {
              error?: string;
              block_reason?: string;
              can_retake?: boolean;
              max_attempts?: number | null;
              attempts_used?: number;
            };
          };
        }
      )?.response?.data;
      const httpStatus = (startError as { response?: { status?: number } })?.response?.status;
      const blockReason = responsePayload?.block_reason;

      const isMaxAttemptsError =
        blockReason === 'max_attempts' ||
        (httpStatus === 400 &&
          responsePayload?.can_retake === false &&
          ((typeof responsePayload?.max_attempts === 'number' &&
            typeof responsePayload?.attempts_used === 'number' &&
            responsePayload.attempts_used >= responsePayload.max_attempts) ||
            (typeof responsePayload?.error === 'string' &&
              responsePayload.error.toLowerCase().includes('max attempts'))));
      const isRetakeBlockedError =
        blockReason === 'retake_disabled_after_pass' ||
        (httpStatus === 400 &&
          typeof responsePayload?.error === 'string' &&
          responsePayload.error.toLowerCase().includes('retakes are disabled'));

      setIsAccessDenied(httpStatus === 403);
      setHasReachedMaxAttempts(isMaxAttemptsError);
      setIsRetakeBlocked(isRetakeBlockedError);
      setError(getErrorMessage(startError));
      setAttemptState(null);
      setQuestionsState([]);
      setResponseDrafts({});
      setServerResponses({});
      responseDraftsRef.current = {};
      serverResponsesRef.current = {};
      saveQueueByQuestionRef.current = new Map();
      saveRevisionByQuestionRef.current = {};
      setCurrentIndex(0);
      setProgressState({
        attemptId: null,
        status: 'not_started',
        currentIndex: 0,
        totalQuestions: 0,
        completedAt: null,
      });
      throw startError;
    } finally {
      setLoading(false);
    }
  }, [flowState, initialFlow, responseDraftsRef, serverResponsesRef]);

  useEffect(() => {
    if (initialBootstrap) {
      setLoading(false);
      setError(initialBootstrap.errorMessage ?? null);
      setIsAccessDenied(false);
      setHasReachedMaxAttempts(initialBootstrap.hasReachedMaxAttempts);
      setIsRetakeBlocked(initialBootstrap.isRetakeBlocked);
      setAutosaveError(null);
      setFlowState(initialBootstrap.flow);
      setIntroState(initialBootstrap.intro);
      setAttemptState(null);
      setQuestionsState([]);
      setResponseDrafts({});
      setServerResponses({});
      responseDraftsRef.current = {};
      serverResponsesRef.current = {};
      saveQueueByQuestionRef.current = new Map();
      saveRevisionByQuestionRef.current = {};
      setCurrentIndex(0);
      setProgressState({
        attemptId: null,
        status: 'not_started',
        currentIndex: 0,
        totalQuestions: 0,
        completedAt: null,
      });
      return;
    }

    void loadIntro();
  }, [initialBootstrap, loadIntro, responseDraftsRef, serverResponsesRef]);

  const goToPreviousQuestion = useCallback(() => {
    setCurrentIndex((previous) => Math.max(previous - 1, 0));
    setProgressState((previous) => ({
      ...previous,
      currentIndex: Math.max(previous.currentIndex - 1, 0),
    }));
  }, []);

  const goToQuestion = useCallback(
    (index: number) => {
      if (Number.isNaN(index)) {
        return;
      }

      const boundedIndex = Math.min(Math.max(index, 0), questionsState.length);
      setCurrentIndex(boundedIndex);
      setProgressState((previous) => ({
        ...previous,
        currentIndex: boundedIndex,
      }));
    },
    [questionsState.length],
  );

  const flushPendingResponseSaves = useCallback(async () => {
    const pendingSaves = Array.from(saveQueueByQuestionRef.current.values());
    if (!pendingSaves.length) {
      return;
    }

    await Promise.allSettled(pendingSaves);
  }, []);

  const submitQuestionResponse = useCallback(
    async (response: ResponsePayload, options?: { advance?: boolean }) => {
      const questionId = response?.question;
      if (!questionId) {
        return;
      }

      const attemptId = attemptState?.id ?? progressState.attemptId;
      if (typeof attemptId !== 'number') {
        throw new Error('Quiz attempt is not active.');
      }

      const existingDraft = responseDraftsRef.current[questionId];
      const question = questionsState.find((candidate) => candidate.id === questionId);
      const normalizedQuestionType = normalizeQuestionType(
        question?.question_type_display ?? question?.question_type ?? '',
      );
      const nextIsCompleted =
        options?.advance === true
          ? true
          : response.is_completed ?? existingDraft?.is_completed ?? false;
      const normalizedResponse: ResponsePayload = {
        ...response,
        question: questionId,
        is_completed: nextIsCompleted,
      };
      setAutosaveError(null);
      const saveRevision = (saveRevisionByQuestionRef.current[questionId] ?? 0) + 1;
      saveRevisionByQuestionRef.current[questionId] = saveRevision;

      commitResponseDrafts((previous) => {
        const draftForQuestion = previous[questionId];
        return {
          ...previous,
          [questionId]: {
            ...draftForQuestion,
            ...normalizedResponse,
            question: questionId,
            is_completed: normalizedResponse.is_completed ?? draftForQuestion?.is_completed ?? false,
          },
        };
      });

      if (options?.advance) {
        const questionIndex = questionsState.findIndex((candidate) => candidate.id === questionId);
        if (questionIndex >= 0) {
          const nextIndex = Math.min(questionIndex + 1, questionsState.length);
          setCurrentIndex(nextIndex);
          setProgressState((previous) => ({
            ...previous,
            currentIndex: nextIndex,
          }));
        }
      }

      const serverResponse = serverResponsesRef.current[questionId];
      if (serverResponse && areResponsesEqual(normalizedResponse, serverResponse, normalizedQuestionType)) {
        commitResponseDrafts((previous) => {
          const currentDraft = previous[questionId];
          if (!currentDraft) {
            return previous;
          }

          const nextResponseId = currentDraft.responseId ?? serverResponse.id;
          const nextFlowItem = currentDraft.flow_item ?? serverResponse.flow_item;
          const nextFlowRun = currentDraft.flow_run ?? serverResponse.flow_run;

          if (
            currentDraft.responseId === nextResponseId &&
            currentDraft.flow_item === nextFlowItem &&
            currentDraft.flow_run === nextFlowRun
          ) {
            return previous;
          }

          return {
            ...previous,
            [questionId]: {
              ...currentDraft,
              responseId: nextResponseId,
              flow_item: nextFlowItem,
              flow_run: nextFlowRun,
            },
          };
        });

        return;
      }

      const runSave = async () => {
        setInFlightSaveCount((previous) => previous + 1);
        try {
          const saveResponse = await quiz.upsertAttemptResponse(attemptId, normalizedResponse);
          const savedResponse = mapApiResponseToStored(saveResponse?.data);
          if (savedResponse && typeof savedResponse.question === 'number') {
            const nextResponses = {
              ...serverResponsesRef.current,
              [savedResponse.question]: savedResponse,
            };
            serverResponsesRef.current = nextResponses;
            setServerResponses(nextResponses);

            const latestRevision = saveRevisionByQuestionRef.current[questionId];
            if (latestRevision === saveRevision) {
              commitResponseDrafts((previous) => {
                const currentDraft = previous[savedResponse.question];
                if (!currentDraft) {
                  return previous;
                }

                if (!areResponsesEqual(currentDraft, normalizedResponse, normalizedQuestionType)) {
                  return previous;
                }

                if (
                  currentDraft.responseId === savedResponse.id &&
                  currentDraft.flow_item === (currentDraft.flow_item ?? savedResponse.flow_item) &&
                  currentDraft.flow_run === (currentDraft.flow_run ?? savedResponse.flow_run)
                ) {
                  return previous;
                }

                return {
                  ...previous,
                  [savedResponse.question]: {
                    ...currentDraft,
                    responseId: savedResponse.id,
                    flow_item: currentDraft.flow_item ?? savedResponse.flow_item,
                    flow_run: currentDraft.flow_run ?? savedResponse.flow_run,
                  },
                };
              });
            }
          }
        } catch (saveError) {
          setAutosaveError(getErrorMessage(saveError));
          throw saveError;
        } finally {
          setInFlightSaveCount((previous) => Math.max(previous - 1, 0));
        }
      };

      const previousSavePromise = saveQueueByQuestionRef.current.get(questionId) ?? Promise.resolve();
      const chainedSavePromise = previousSavePromise
        .catch(() => undefined)
        .then(runSave);

      saveQueueByQuestionRef.current.set(questionId, chainedSavePromise);

      return chainedSavePromise.finally(() => {
        if (saveQueueByQuestionRef.current.get(questionId) === chainedSavePromise) {
          saveQueueByQuestionRef.current.delete(questionId);
        }
      });
    },
    [attemptState?.id, commitResponseDrafts, progressState.attemptId, questionsState, responseDraftsRef, serverResponsesRef],
  );

  const submitQuiz = useCallback(
    async (options?: SubmitQuizOptions) => {
      if (submitQuizPromiseRef.current) {
        throw new Error('Quiz submission is already in progress.');
      }

      const attemptId = attemptState?.id ?? progressState.attemptId;
      if (typeof attemptId !== 'number') {
        throw new Error('Quiz attempt is not active.');
      }

      const submissionPromise = (async () => {
        setIsSubmitting(true);
        setError(null);

        try {
          await flushPendingResponseSaves();

          const latestDrafts = responseDraftsRef.current;
          const missingQuestionIds: number[] = [];
          const submissionPayload: QuizResponse[] = [];

          questionsState.forEach((question) => {
            const questionId = question.id;
            if (typeof questionId !== 'number') {
              return;
            }

            const draft = latestDrafts[questionId];
            if (!draft || !isQuestionResponseValid(question, { draft })) {
              missingQuestionIds.push(questionId);
              return;
            }

            submissionPayload.push(buildSubmissionResponse(question, draft));
          });

          if (missingQuestionIds.length > 0) {
            throw new Error('All quiz questions must be answered before submission.');
          }

          const response = await quiz.submitQuiz(attemptId, submissionPayload, {
            includeQuestionReview: options?.includeQuestionReview ?? true,
          });
          const submissionResult = response?.data as QuizSubmissionResponse;
          const completedAtValue = submissionResult?.attempt?.completed_at ?? null;

          if (submissionResult?.attempt) {
            setAttemptState(submissionResult.attempt);
          }
          setProgressState((previous) => ({
            ...previous,
            status: 'completed',
            currentIndex: questionsState.length,
            totalQuestions: questionsState.length,
            completedAt: completedAtValue ?? previous.completedAt,
          }));

          return submissionResult;
        } catch (submissionError) {
          setError(getErrorMessage(submissionError));
          throw submissionError;
        } finally {
          submitQuizPromiseRef.current = null;
          setIsSubmitting(false);
        }
      })();

      submitQuizPromiseRef.current = submissionPromise;
      return submissionPromise;
    },
    [attemptState?.id, flushPendingResponseSaves, progressState.attemptId, questionsState, responseDraftsRef],
  );

  const currentQuestion = useMemo(() => {
    if (currentIndex >= questionsState.length) {
      return null;
    }

    return questionsState[currentIndex];
  }, [currentIndex, questionsState]);

  const hasUnsavedChanges = useMemo(
    () =>
      progressState.status !== 'completed' &&
      questionsState.some((question) => {
        const questionId = question.id;
        if (typeof questionId !== 'number') {
          return false;
        }

        const draft = responseDrafts[questionId];
        if (!draft) {
          return false;
        }

        const serverResponse = serverResponses[questionId];
        const normalizedQuestionType = normalizeQuestionType(
          question.question_type_display ?? question.question_type ?? '',
        );

        return !serverResponse || !areResponsesEqual(draft, serverResponse, normalizedQuestionType);
      }),
    [progressState.status, questionsState, responseDrafts, serverResponses],
  );

  const isFlowCompleted = progressState.status === 'completed' || Boolean(progressState.completedAt);
  const isSavingResponse = inFlightSaveCount > 0;
  const hasStartedAttempt = progressState.status === 'in_progress' || Boolean(attemptState);
  const isQuizEntryBlocked = hasReachedMaxAttempts || isRetakeBlocked;
  const clearAutosaveError = useCallback(() => {
    setAutosaveError(null);
  }, []);

  return {
    loading,
    error,
    autosaveError,
    isAccessDenied,
    hasReachedMaxAttempts,
    isRetakeBlocked,
    isQuizEntryBlocked,
    hasStartedAttempt,
    flow: flowState,
    intro: introState,
    attempt: attemptState,
    questions: questionsState,
    currentQuestionIndex: currentIndex,
    currentQuestion,
    responsesByQuestion: responseDrafts,
    hasUnsavedChanges,
    isSubmitting,
    isSavingResponse,
    isFlowCompleted,
    progress: progressState,
    loadIntro,
    startOrResumeAttempt,
    goToPreviousQuestion,
    goToQuestion,
    clearAutosaveError,
    submitQuestionResponse,
    submitQuiz,
  };
};
